/**
 * A searchable list of the feats, skill increases or spells a pick offers, shared by the stepped builder,
 * the level-up pick modal and the town's retrain panel. Filters: search, category, trait, level and an
 * "eligible only" toggle (on by default). Ineligible options stay in the list, greyed out with the reason.
 */
import type { Codex } from '../shared/view.ts';
import { blockHtml, refAttr } from './rules.ts';

export interface PickOpt {
  id: string;
  eligible: boolean;
  reason?: string;
  category?: string;
  level?: number;
}

export interface ListState {
  search: string;
  category: string;
  trait: string;
  level: string;
  eligibleOnly: boolean;
  /** The row whose rules are open (modal mode). */
  focus?: string;
}

export const newListState = (): ListState => ({ search: '', category: '', trait: '', level: '', eligibleOnly: true });

export interface ListCfg {
  spell: boolean;
  codex: Codex | null;
  /** 'choose': a click takes the option (builder). 'focus': a click opens its rules, with a button to take it (modal). */
  mode: 'choose' | 'focus';
  /** The data attribute (name without `data-`) a taking click carries, and its value for an option. */
  attr: string;
  value: (id: string) => string;
  /** Options the build has chosen: always listed, marked. */
  chosen?: string[];
  /** Chosen options that are no longer valid: highlighted as stale. */
  stale?: string[];
  /** The label of the taking button in focus mode. */
  takeLabel?: string;
}

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

interface Info { id: string; name: string; type: string; level: number; traits: string[]; rarity: string; category: string; eligible: boolean; reason?: string; ref: string }

function infoOf(o: PickOpt, cfg: ListCfg): Info {
  const ref = `${cfg.spell ? 'spell' : 'feat'}:${o.id}`;
  const b = cfg.codex?.[ref];
  const typeLevel = b ? /(\d+)$/.exec(b.type)?.[1] : undefined;
  const level = o.level ?? (typeLevel !== undefined ? Number(typeLevel) : b?.type === 'Cantrip' ? 0 : 0);
  const category = o.category ?? (cfg.spell ? 'spell' : b?.type === 'Skill Increase' ? 'skill-increase' : 'feat');
  return { id: o.id, name: b?.name ?? o.id, type: b?.type ?? '', level, traits: b?.traits ?? [], rarity: b?.rarity ?? 'common', category, eligible: o.eligible, reason: o.reason, ref };
}

const CATEGORY_LABEL: Record<string, string> = {
  ancestry: 'Ancestry', class: 'Class', skill: 'Skill', general: 'General', 'skill-increase': 'Skill increase', spell: 'Spell', archetype: 'Archetype', bonus: 'Bonus',
};
const categoryLabel = (c: string) => CATEGORY_LABEL[c] ?? c.replace(/-/g, ' ').replace(/^./, (x) => x.toUpperCase());

/** The options the filters leave, in level then name order (chosen ones always stay). */
function visible(opts: PickOpt[], state: ListState, cfg: ListCfg): Info[] {
  const q = state.search.trim().toLowerCase();
  const chosen = new Set(cfg.chosen ?? []);
  return opts.map((o) => infoOf(o, cfg)).filter((i) => {
    if (chosen.has(i.id)) return true;
    if (state.eligibleOnly && !i.eligible) return false;
    if (state.category && i.category !== state.category) return false;
    if (state.trait && !i.traits.includes(state.trait)) return false;
    if (state.level !== '' && i.level !== Number(state.level)) return false;
    if (q && !i.name.toLowerCase().includes(q) && !i.traits.some((t) => t.includes(q))) return false;
    return true;
  }).sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
}

/** The search box and filters. Each control carries `data-pl-field`; `applyControl` reads them back into the state. */
export function controlsHtml(opts: PickOpt[], state: ListState, cfg: ListCfg, key: string): string {
  const all = opts.map((o) => infoOf(o, cfg));
  const categories = [...new Set(all.map((i) => i.category))].sort();
  const traits = [...new Set(all.flatMap((i) => i.traits))].sort();
  const levels = [...new Set(all.map((i) => i.level))].sort((a, b) => a - b);
  const select = (field: string, label: string, values: { v: string; text: string }[], current: string) =>
    values.length < 2 ? '' : `<select data-pl="${esc(key)}" data-pl-field="${field}" aria-label="${label}"><option value="">${label}</option>${values.map((x) => `<option value="${esc(x.v)}" ${x.v === current ? 'selected' : ''}>${esc(x.text)}</option>`).join('')}</select>`;
  const eligibleCount = all.filter((i) => i.eligible).length;
  return `<div class="pl-controls">
    <input type="search" data-pl="${esc(key)}" data-pl-field="search" value="${esc(state.search)}" placeholder="Search by name or trait" aria-label="Search" autocomplete="off">
    ${select('category', 'Any category', categories.map((c) => ({ v: c, text: categoryLabel(c) })), state.category)}
    ${select('trait', 'Any trait', traits.map((t) => ({ v: t, text: t })), state.trait)}
    ${select('level', 'Any level', levels.map((l) => ({ v: String(l), text: cfg.spell ? (l === 0 ? 'Cantrips' : `Rank ${l}`) : `Level ${l}` })), state.level)}
    <label class="pl-toggle"><input type="checkbox" data-pl="${esc(key)}" data-pl-field="eligible" ${state.eligibleOnly ? 'checked' : ''}> Eligible only <span class="muted">(${eligibleCount}/${all.length})</span></label>
  </div>`;
}

/** Reads a changed control into the state. Returns true if the element was one of the list's controls. */
export function applyControl(state: ListState, el: HTMLElement): boolean {
  const field = el.dataset.plField;
  if (!field) return false;
  const input = el as HTMLInputElement;
  if (field === 'search') state.search = input.value;
  else if (field === 'eligible') state.eligibleOnly = input.checked;
  else if (field === 'category' || field === 'trait' || field === 'level') state[field] = input.value;
  return true;
}

/** The list's rows. */
export function listHtml(opts: PickOpt[], state: ListState, cfg: ListCfg): string {
  const rows = visible(opts, state, cfg);
  if (!rows.length) return '<div class="pl-empty muted">Nothing matches. Clear the search or untick "Eligible only".</div>';
  const chosen = new Set(cfg.chosen ?? []);
  const stale = new Set(cfg.stale ?? []);
  return rows.map((i) => {
    const isChosen = chosen.has(i.id);
    const focused = cfg.mode === 'focus' && state.focus === i.id;
    const cls = ['pl-row', i.rarity, i.eligible ? '' : 'ineligible', isChosen ? 'selected' : '', stale.has(i.id) ? 'stale' : '', focused ? 'focused' : ''].filter(Boolean).join(' ');
    const meta = [i.type, i.traits.slice(0, 5).join(', ')].filter(Boolean).join(' · ');
    const reason = !i.eligible && i.reason ? `<span class="pl-reason">${esc(i.reason)}</span>` : stale.has(i.id) ? `<span class="pl-reason">${esc(i.reason ?? 'No longer valid with your other choices')}</span>` : '';
    const rarity = i.rarity !== 'common' ? `<span class="trait ${esc(i.rarity)}">${esc(i.rarity)}</span>` : '';
    const head = cfg.mode === 'choose'
      ? `<button type="button" class="pl-main" ${i.eligible || isChosen ? `data-${cfg.attr}="${esc(cfg.value(i.id))}"` : 'disabled'}>`
      : `<button type="button" class="pl-main" data-pl-focus="${esc(i.id)}">`;
    const block = cfg.codex?.[i.ref];
    const detail = (focused || (cfg.mode === 'choose' && isChosen)) && block
      ? `<div class="pl-detail">${blockHtml(block, { compact: true })}${cfg.mode === 'focus' && i.eligible ? `<button type="button" class="primary" data-${cfg.attr}="${esc(cfg.value(i.id))}">${esc(cfg.takeLabel ?? 'Take')}</button>` : ''}</div>`
      : '';
    return `<div class="${cls}"${cfg.mode === 'choose' ? refAttr(i.ref) : ''}>${head}<span class="pl-name">${esc(i.name)} ${rarity}</span><span class="pl-meta">${esc(meta)}</span>${reason}</button>${detail}</div>`;
  }).join('');
}
